import { EVENT_NAME as NOTIFICATION_EVENT } from '@adobe-fonts/site/events/NotificationEvent.js';
import VariationFavoriteEvent from '@adobe-fonts/site/events/VariationFavoriteEvent.js';
import { getService as service } from '../../util/angular_helpers.js';
import { getData } from '../../util/script_data.js';
import { addFavorite, removeFavorite } from '../../util/variation_favorite.js';

export class LibrariesToolbarFavorite {
  constructor(element) {
    this.element = element;
    this.librariesToolbar = element._librariesToolbar;
  }

  addFavorite = async (variationIds) => {
    return await addFavorite(variationIds);
  };

  /**
   * Decorator to handle errors while making a remote call to the backend to add/remove favorites.
   *
   * @param {Function} favoriteAction The function to execute to add/remove favorites.
   * @param {Array} variationIds
   * @returns {Promise} Resolved with the response body, or rejected with an error
   */
  _withErrorHandling = async (favoriteAction, variationIds) => {
    const response = await favoriteAction(variationIds);
    const responseBody = await response.json();
    if (!response.ok || !responseBody.success) {
      throw new Error('Failed to add/remove favorites');
    }
    return responseBody;
  };

  handleAuthenticationWithPostLoginAction = async (action, data) => {
    const AuthenticationService = service('AuthenticationService');
    await AuthenticationService.handleAuthenticationWithPostLoginAction(action, data);
  };

  getI18n = () => {
    return ((getData('/neue/i18n') || {})['neue.browse.favorites'] || {});
  };

  handleFavoriteEvent = async (event) => {
    const toolbar = event.target.closest('af-libraries-toolbar');
    if (!toolbar || toolbar !== this.librariesToolbar) {
      return;
    }

    if (event.detail.addVariationIds) {
      await this.handleAddFavorites(event.detail.addVariationIds);
    }
    if (event.detail.removeVariationIds) {
      await this.handleRemoveFavorites(event.detail.removeVariationIds);
    }
    if (event.target.closeRemoveFromFavoritesDialog) {
      event.target.closeRemoveFromFavoritesDialog();
    }
  };

  handleAddFavorites = async (variationIds) => {
    const i18n = this.getI18n();
    let notificationEventParams;
    try {
      await this.handleAuthenticationWithPostLoginAction('favorite_fonts', { font_ids: variationIds });
      await this._withErrorHandling(this.addFavorite, variationIds);

      notificationEventParams = {
        type: 'success',
        message: i18n['add_favorite_success']
      };
      this.librariesToolbar.favorited = true;
      this.updateVariationCards(variationIds, true);
    } catch (error) {
      notificationEventParams = {
        type: 'error',
        message: i18n['add_favorite_failure']
      };
    }
    this.sendNotification(notificationEventParams);
  };

  handleRemoveFavorites = async (variationIds) => {
    const i18n = this.getI18n();
    let notificationEventParams;
    try {
      const responseBody = await this._withErrorHandling(this.removeFavorite, variationIds);
      notificationEventParams = {
        type: 'success',
        message: i18n['remove_favorite_success'],
        options: {
          actionLabel: i18n['remove_favorite_undo'],
          callbackAction: () => this.handleUndoClick(responseBody.font_variation_ids)
        }
      };
      this.librariesToolbar.favorited = false;
      this.updateVariationCards(variationIds, false);
    } catch (error) {
      notificationEventParams = {
        type: 'error',
        message: i18n['remove_favorite_failure']
      };
    }
    this.sendNotification(notificationEventParams);
  };

  handleUndoClick = (variationIds) => {
    this.librariesToolbar.dispatchEvent(new VariationFavoriteEvent({ addVariationIds: variationIds }));
  };

  sendNotification = (params) => {
    window.dispatchEvent(new CustomEvent(NOTIFICATION_EVENT, { detail: params }));
  };

  removeFavorite = async (variationIds) => {
    return await removeFavorite(variationIds);
  };

  /**
   * Updates the variation card's favorite state, so that it stays consistent
   * with the favorite/unfavorite actions from the toolbar.
   *
   * @param {Array} ids Variation ids of cards that need updating
   * @param {boolean} selected State to update card to
   */
  updateVariationCards = (ids, selected) => {
    Array.from(this.element._variationCards)
      .filter(card => ids.includes(parseInt(card.dataset.id)))
      .map(card => card.querySelector('af-favorite-button'))
      .forEach(favoriteButton => {
        favoriteButton.toggleAttribute('selected', selected);
      });
  };
}

export function registerEventHandlers(element) {
  const librariesToolbarFavorite = new LibrariesToolbarFavorite(element);
  element.addEventListener(VariationFavoriteEvent.EVENT_NAME, librariesToolbarFavorite.handleFavoriteEvent);
}
